import { Alert, Card, Group, Select, Skeleton, Stack, Table, Text } from "@mantine/core";
import { IconAlertTriangle, IconFileInvoice, IconReceiptOff } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { PlatformInvoiceApi } from "../../../apis";
import type { PlatformInvoice, PlatformInvoiceStatus } from "../../../apis/platformInvoice";
import { EmptyState } from "../../../components/billing/EmptyState";
import { InvoiceStatusBadge } from "../../../components/billing/InvoiceStatusBadge";
import { MoneyCell } from "../../../components/billing/MoneyCell";
import { TenantSelect } from "../../../components/billing/TenantSelect";
import { PageLayout } from "../../../components/layout";
import { formatDate } from "../../../utils/format";

const STATUSES: PlatformInvoiceStatus[] = ["DRAFT", "ISSUED", "SENT", "PARTIALLY_PAID", "PAID", "OVERDUE", "VOID"];

const breadcrumb = [
  { to: "/", label: "Эхлэл" },
  { to: "/billing", label: "Billing" },
  { label: "Платформ нэхэмжлэх" },
];

const InvoicesList = () => {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState("");
  const [status, setStatus] = useState<PlatformInvoiceStatus | "">("");

  const swrKey = tenantId ? `swr.billing.invoices.${tenantId}.${status}` : null;
  const { data, isLoading, error } = useSWR(swrKey, () =>
    PlatformInvoiceApi.list(tenantId, { status: status || undefined, page: 1, limit: 50 })
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any;
  const rows: PlatformInvoice[] = Array.isArray(raw) ? raw : (raw?.rows ?? []);

  return (
    <PageLayout
      title="Платформ нэхэмжлэх"
      subTitle="Tenant-ийн платформ нэхэмжлэхийн жагсаалт"
      breadcrumb={breadcrumb}
    >
      <Stack gap="md">
        <Group gap="sm">
          <TenantSelect value={tenantId} onChange={setTenantId} placeholder="Тенант сонгох" />
          <Select
            placeholder="Бүх төлөв"
            clearable
            data={STATUSES.map((s) => ({ value: s, label: s }))}
            value={status || null}
            onChange={(v) => setStatus((v as PlatformInvoiceStatus) ?? "")}
            w={200}
          />
        </Group>

        {!tenantId ? (
          <Card withBorder radius="md" padding="md">
            <EmptyState icon={<IconFileInvoice size={26} />} message="Нэхэмжлэх харахын тулд тенант сонгоно уу." />
          </Card>
        ) : error ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>Нэхэмжлэх ачааллахад алдаа гарлаа.</Alert>
        ) : isLoading ? (
          <Skeleton height={220} radius="md" />
        ) : rows.length === 0 ? (
          <Card withBorder radius="md" padding="md">
            <EmptyState icon={<IconReceiptOff size={26} />} message="Нэхэмжлэх олдсонгүй." />
          </Card>
        ) : (
          <Card withBorder radius="md" padding={0}>
            <Table.ScrollContainer minWidth={620}>
              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Дугаар</Table.Th>
                    <Table.Th>Хугацаа</Table.Th>
                    <Table.Th ta="right">Нийт дүн</Table.Th>
                    <Table.Th ta="right">Үлдэгдэл</Table.Th>
                    <Table.Th>Төлөв</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((inv) => (
                    <Table.Tr
                      key={inv.id}
                      onClick={() => navigate(`/billing/invoices/${inv.id}`)}
                      style={{ cursor: "pointer" }}
                    >
                      <Table.Td><Text style={{ fontVariantNumeric: "tabular-nums" }}>{inv.invoiceNumber}</Text></Table.Td>
                      <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
                      </Table.Td>
                      <Table.Td ta="right"><MoneyCell value={inv.totalAmount} currency={inv.currency} /></Table.Td>
                      <Table.Td ta="right"><MoneyCell value={inv.amountDue} currency={inv.currency} /></Table.Td>
                      <Table.Td><InvoiceStatusBadge status={inv.status} /></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card>
        )}

        {rows.length > 0 && (
          <Text size="sm" c="dimmed">Нийт: {rows.length}</Text>
        )}
      </Stack>
    </PageLayout>
  );
};

export default InvoicesList;
